/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { ReactNode, useMemo, useRef } from 'react';

interface Props {
  /**
   * ClassName to be applied to the Ellipse Effect
   */
  className?: string;
  /**
   * Variant of the Ellipse Effect
   */
  variant: 'hover' | 'selected' | 'hidden';
  /**
   * Children of the Ellipse
   */
  children: ReactNode;
  /**
   * Custom size of the Ellipse, this will be the hover variant size, and the selected variant size will be calculated accordingly
   *
   * Default sizes are (px):
   * hover: 44, selected: 56
   */
  size?: number;
}

const defaultHoverSize = 44;
const defaultSelectedSize = 56;
const selectedProportionFactor = defaultSelectedSize / defaultHoverSize;

const EllipseEffect = ({ className = '', variant, size, children }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const motionProps: AnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  };

  const ellipseSize = useMemo(() => {
    if (variant === 'selected') {
      return size ? size * selectedProportionFactor : defaultSelectedSize;
    }
    return size || defaultHoverSize;
  }, [variant, size]);

  return (
    <div
      ref={ref}
      className={classNames('relative flex items-center justify-center', {
        'hover:*:opacity-100': variant === 'hover',
      })}
    >
      {children}
      {variant !== 'hidden' && (
        <AnimatePresence>
          <motion.div
            key={variant}
            {...(variant === 'selected' ? motionProps : {})}
            className={classNames('absolute', 'pointer-events-none', 'flex items-center justify-center', className)}
            style={{ width: ellipseSize, height: ellipseSize }}
          >
            <div
              className={classNames('rounded-full bg-secondary-softlight', {
                'size-[28%] blur-[10px]': variant === 'selected',
                'size-1/2 opacity-60 blur-[6px]': variant === 'hover',
              })}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default EllipseEffect;
